import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Typogaphy } from 'vnetwork-ui'
import { Globe, Lock, Users, Info } from 'lucide-react'

interface EmployeeOption {
    value: number
    label: string
}

interface ReviewSharingCardProps {
    shareScope: string
    employees: EmployeeOption[]
    departmentIds: number[]
    getDepartmentName: (id: string | number) => string
}

const ReviewSharingCard: React.FC<ReviewSharingCardProps> = ({
    shareScope,
    employees,
    departmentIds,
    getDepartmentName,
}) => {
    const { t } = useTranslation('asset')

    return (
        <Card className="border-gray-100 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-gray-50 pb-3">
                <CardTitle className="flex items-center gap-2">
                    <Typogaphy className="font-semibold text-primary">
                        {t('form.sharing')}
                    </Typogaphy>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                        {t('form.shareScope')}:
                    </span>
                    <Badge
                        variant={shareScope === 'public' ? 'success' : 'info'}
                        className="flex items-center gap-1"
                    >
                        {shareScope === 'public' ? (
                            <Globe className="w-3 h-3" />
                        ) : (
                            <Lock className="w-3 h-3" />
                        )}
                        {shareScope === 'public'
                            ? t('form.sharePublic')
                            : t('form.sharePrivate')}
                    </Badge>
                </div>

                {shareScope === 'private' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-50">
                        <div>
                            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {t('form.shareWithEmployees')}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {employees.length > 0 ? (
                                    employees.map((emp) => (
                                        <Badge
                                            key={emp.value}
                                            variant="outline"
                                            className="bg-gray-50"
                                        >
                                            {emp.label}
                                        </Badge>
                                    ))
                                ) : (
                                    <span className="text-sm text-gray-400 italic">
                                        {t('form.notSelected')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2 flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                {t('form.shareWithDepartments')}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {departmentIds.length > 0 ? (
                                    departmentIds.map((deptId) => (
                                        <Badge
                                            key={deptId}
                                            variant="outline"
                                            className="bg-gray-50"
                                        >
                                            {getDepartmentName(deptId)}
                                        </Badge>
                                    ))
                                ) : (
                                    <span className="text-sm text-gray-400 italic">
                                        {t('form.notSelected')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ReviewSharingCard
